import { useState, useEffect } from 'react'
import { listSurveyResponses, getSurveyStats } from '../services/survey-api'
import { getQuestions } from '../services/survey-question-api'
import type { SurveyResponseItem, SurveyStats, SurveyQuestion } from '../types/survey.types'

interface UseSurveyResponsesState {
  responses: SurveyResponseItem[]
  stats: SurveyStats | null
  questions: SurveyQuestion[]
  loading: boolean
  error: Error | null
  refresh: () => void
}

export function useSurveyResponses(surveyId: string | null): UseSurveyResponsesState {
  const [responses, setResponses] = useState<SurveyResponseItem[]>([])
  const [stats, setStats] = useState<SurveyStats | null>(null)
  const [questions, setQuestions] = useState<SurveyQuestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!surveyId) {
      setResponses([])
      setStats(null)
      setQuestions([])
      return
    }

    let cancelled = false

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        const [responseData, statsData, questionData] = await Promise.all([
          listSurveyResponses({ surveyId }),
          getSurveyStats(surveyId),
          getQuestions(surveyId)
        ])
        if (!cancelled) {
          setResponses(responseData)
          setStats(statsData)
          setQuestions(questionData)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch survey responses'))
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [refreshKey, surveyId])

  const refresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  return { responses, stats, questions, loading, error, refresh }
}
